/**
 * I blocchi di dettaglio della BI: prodotti, ordini in perdita, paesi,
 * post-vendita, costi AllService e stati. Ognuno con la sua «i».
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { prettifyApiError } from "@/shared/api";
import {
  fetchAsCosts,
  localIsoDate,
  type AsCostMonth,
  type BiDettagli,
  type BiProdotto,
  type BiStatusRow,
} from "../biApi";
import { dataBreve, eur, intero, logoCanale, motivo, n, nomeCanale, perc } from "../biTesti";
import { Incerto, InfoTip } from "./InfoTip";
import { Pillole, Riquadro } from "./Riquadro";

// ── Prodotti ─────────────────────────────────────────────────────────────────

type VistaProdotti = "venduti" | "margine" | "peggiori";

export function Prodotti({ p }: { p: BiDettagli["prodotti"] }) {
  const [vista, setVista] = useState<VistaProdotti>("venduti");
  const righe: BiProdotto[] = (
    vista === "venduti" ? p.top_revenue : vista === "margine" ? p.top_margin : p.worst_margin
  ).slice(0, 10);
  return (
    <Riquadro
      titolo="Prodotti"
      spiegazione={{
        titolo: "Prodotti",
        testo: `${intero(p.sku_count)} articoli venduti nel periodo. Nell'ordine con piu' articoli, netto e margine sono divisi fra le righe in proporzione al prezzo: e' una stima, segnata con «diviso».`,
        conto: [
          { etichetta: "Netto articolo", valore: "netto ordine × prezzo riga ÷ prezzo ordine" },
          { etichetta: "Margine articolo", valore: "margine ordine × stessa quota" },
        ],
      }}
      azioni={
        <Pillole
          etichetta="Quali prodotti"
          valore={vista}
          onChange={setVista}
          opzioni={[
            { id: "venduti", nome: "Piu' venduti" },
            { id: "margine", nome: "Piu' margine" },
            { id: "peggiori", nome: "Meno margine" },
          ]}
        />
      }
    >
      {righe.length === 0 ? (
        <p className="text-[13px]">Nessun prodotto nel periodo.</p>
      ) : (
        <ol className="divide-y divide-line">
          {righe.map((r, i) => (
            <li
              key={r.sku}
              className="grid grid-cols-[22px_minmax(0,1fr)_auto_auto] items-center gap-3 py-2 text-[13px] max-md:grid-cols-[minmax(0,1fr)_auto]"
            >
              <span className="font-bold tabular-nums max-md:hidden">{i + 1}</span>
              <span className="min-w-0">
                <span className="block truncate font-bold">{r.title || r.sku}</span>
                <span className="flex flex-wrap items-center gap-1.5 text-[12px] font-semibold">
                  <span className="font-mono">{r.sku}</span>· {intero(r.quantity)} pezzi ·{" "}
                  {intero(r.orders)} ordini
                  {r.split && <span className="rounded-full bg-paper-2 px-1.5">diviso</span>}
                  <Incerto
                    stimati={n(r.estimate_count)}
                    mancanti={n(r.not_computable_count)}
                    compatto
                  />
                </span>
              </span>
              <span className="text-right font-semibold tabular-nums max-md:hidden">
                {eur(r.net_revenue)}
              </span>
              <span
                className={`min-w-[84px] text-right font-bold tabular-nums ${n(r.margin) < 0 ? "text-red" : ""}`}
              >
                {r.margin == null ? "—" : `${n(r.estimate_count) > 0 ? "≈ " : ""}${eur(r.margin)}`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Riquadro>
  );
}

// ── Ordini in perdita ────────────────────────────────────────────────────────

export function InPerdita({ righe }: { righe: BiDettagli["in_perdita"] }) {
  return (
    <Riquadro
      titolo="Ordini in perdita"
      spiegazione={{
        titolo: "Ordini in perdita",
        testo:
          "I 20 ordini dove i costi superano il fatturato netto, dal peggiore. Aprili per vedere ogni costo. Se c'e' «≈», un costo e' stimato e la perdita potrebbe non essere vera.",
      }}
    >
      {righe.length === 0 ? (
        <p className="text-[13px]">Nessun ordine in perdita nel periodo.</p>
      ) : (
        <ul className="divide-y divide-line">
          {righe.map((o) => (
            <li key={o.order_id}>
              <Link
                to={`/ordini/${encodeURIComponent(o.reference)}`}
                className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-[10px] px-2 py-2 text-[13px] transition-colors hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <MarketplaceLogo {...logoCanale(o.marketplace)} size={18} />
                  <span className="min-w-0">
                    <span className="block truncate font-bold underline-offset-2 group-hover:underline">
                      {o.reference}
                    </span>
                    <span className="block truncate text-[12px] font-semibold">
                      {nomeCanale(o.marketplace)} · {dataBreve(o.order_date)} · netto{" "}
                      {eur(o.net_revenue, 2)}
                      {o.is_estimate && o.estimate_reasons.length > 0 && (
                        <>
                          {" "}
                          · ≈{" "}
                          {o.estimate_reasons.map((m) => motivo(m).titolo.toLowerCase()).join(", ")}
                        </>
                      )}
                    </span>
                  </span>
                </span>
                <span className="text-right font-bold tabular-nums text-red">
                  {o.is_estimate && "≈ "}
                  {eur(o.margin, 2)}
                  <span className="block text-[11.5px] font-semibold text-ink">
                    {perc(o.margin_percent == null ? null : n(o.margin_percent))}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Riquadro>
  );
}

// ── Paesi ────────────────────────────────────────────────────────────────────

const PAESI: Record<string, string> = {
  IT: "Italia",
  ES: "Spagna",
  FR: "Francia",
  DE: "Germania",
  AT: "Austria",
  BE: "Belgio",
  NL: "Paesi Bassi",
  CH: "Svizzera",
  PT: "Portogallo",
};

export function Paesi({ righe }: { righe: BiDettagli["paesi"] }) {
  const tot = righe.reduce((s, r) => s + n(r.orders), 0) || 1;
  return (
    <Riquadro
      titolo="Paesi"
      spiegazione={{
        titolo: "Paesi",
        testo:
          "Il paese di consegna quando il canale lo dice (Kery). Per i marketplace e' il paese del negozio (Amazon ES = Spagna): lo stesso usato per togliere l'IVA.",
      }}
    >
      <ul className="space-y-2.5">
        {righe.map((r) => (
          <li key={r.country} className="text-[13px]">
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 font-bold">
                {PAESI[r.country] ?? r.country}
                {r.from_shop && (
                  <span className="rounded-full bg-paper-2 px-1.5 text-[11.5px] font-semibold">
                    dal negozio
                  </span>
                )}
                <Incerto stimati={0} mancanti={n(r.not_computable_count)} compatto />
              </span>
              <span className="font-semibold tabular-nums">
                {intero(r.orders)} ordini · {eur(r.net_revenue)}
              </span>
            </div>
            <span
              className="mt-1 block h-1.5 overflow-hidden rounded-full bg-paper-2"
              aria-hidden="true"
            >
              <span
                className="bi-cresce block h-full rounded-full bg-blue"
                style={{ width: `${(n(r.orders) / tot) * 100}%` }}
              />
            </span>
          </li>
        ))}
      </ul>
    </Riquadro>
  );
}

// ── Post-vendita ─────────────────────────────────────────────────────────────

export function PostVendita({ d }: { d: BiDettagli }) {
  const p = d.post_vendita;
  const voci = [
    {
      nome: "Ordini con reso",
      valore: intero(p.returned_orders),
      sotto: `${eur(p.returned_net_revenue)} di fatturato netto perso`,
      spiega:
        "Ordini del periodo con un reso aperto o chiuso (non annullato). Sono fuori da fatturato e margine.",
    },
    {
      nome: "Rimborsi",
      valore: eur(p.refund_net),
      sotto: `${intero(p.refund_count)} note di credito`,
      spiega:
        "Note di credito degli ordini del periodo. Senza IVA: rimborso × netto ÷ lordo dello stesso ordine.",
      incerto:
        n(p.refund_missing_count) > 0
          ? `${intero(p.refund_missing_count)} rimborsi senza netto dell'ordine: non sommati.`
          : undefined,
    },
    {
      nome: "Rimborsato dai fornitori",
      valore: eur(p.supplier_refund_net),
      sotto: `${intero(p.supplier_refund_count)} note di credito fornitore`,
      spiega:
        "Quanto ci hanno restituito Itamoby e Ityhome sui resi del periodo, senza IVA: l'imponibile della loro nota di credito, letta dalla mail. Una nota che non si collega a un ordine non e' contata.",
    },
    {
      nome: "Annullati",
      valore: intero(p.cancelled_orders),
      sotto: "fuori dai conti",
      spiega: "Ordini annullati, rifiutati, con errore di pagamento o rimborsati per intero.",
    },
    d.costi_post_vendita
      ? {
          nome: "Sostituzioni",
          valore: eur(d.costi_post_vendita.replacements_total),
          sotto: `${intero(d.costi_post_vendita.replacement_orders)} ordini con un pezzo rimandato`,
          spiega:
            "Pezzi dalle fatture di ricambio Itamoby e Ityhome, AllService scritto a mano diviso 1,22, etichette dalle fatture corriere. Senza IVA. Non e' dentro il margine: e' nella scheda «Resi e sostituzioni».",
          incerto: undefined,
        }
      : {
          nome: "Sostituzioni",
          valore: eur(d.economia.replacements_cost_net),
          sotto: `${intero(d.economia.replacements_count)} pezzi rimandati`,
          spiega:
            "Costo dei pezzi rimandati, senza IVA: Itamoby e Ityhome dalla loro mail, AllService scritto a mano e diviso 1,22. Non e' dentro il margine.",
          incerto: undefined,
        },
  ];
  return (
    <Riquadro
      titolo="Dopo la vendita"
      spiegazione={{
        titolo: "Dopo la vendita",
        testo:
          "Cosa e' successo agli ordini del periodo dopo l'acquisto. Contano gli ordini per data d'ordine, non per data del reso.",
      }}
    >
      <ul className="grid grid-cols-2 gap-3">
        {voci.map((v, i) => (
          <li
            key={v.nome}
            className={`rounded-[14px] bg-paper px-3.5 py-3 ${
              voci.length % 2 === 1 && i === voci.length - 1 ? "col-span-2" : ""
            }`}
          >
            <span className="flex items-center justify-between gap-1 text-[12.5px] font-bold">
              {v.nome}
              <InfoTip spiegazione={{ titolo: v.nome, testo: v.spiega, incerto: v.incerto }} />
            </span>
            <span className="mt-1 block font-display text-[22px] font-bold leading-none tabular-nums">
              {v.valore}
            </span>
            <span className="mt-1 block text-[12px] font-semibold">{v.sotto}</span>
          </li>
        ))}
      </ul>
    </Riquadro>
  );
}

// ── Costi AllService ─────────────────────────────────────────────────────────

const VOCI_AS: { chiave: keyof AsCostMonth; nome: string }[] = [
  { chiave: "picking_cost", nome: "Preparazione" },
  { chiave: "spazio_cost", nome: "Spazio" },
  { chiave: "entrata_cost", nome: "Entrata merce" },
  { chiave: "etichette_cost", nome: "Etichette" },
  { chiave: "reso_cost", nome: "Resi" },
  { chiave: "canone_cost", nome: "Canone" },
];

export function CostiAllService() {
  const [mesi, setMesi] = useState<AsCostMonth[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    fetchAsCosts()
      .then((r) => vivo && setMesi(r.months))
      .catch((e: unknown) => vivo && setErrore(prettifyApiError(e)));
    return () => {
      vivo = false;
    };
  }, []);
  // Il foglio prefattura ha gia' le righe dei mesi futuri, a zero: non sono costi.
  const meseCorrente = `${localIsoDate(new Date()).slice(0, 7)}-01`;
  const ultimi = (mesi ?? []).filter((m) => m.month <= meseCorrente).slice(-6);
  const massimo = Math.max(...ultimi.map((m) => n(m.total_cost)), 1);
  return (
    <Riquadro
      titolo="Costi AllService"
      spiegazione={{
        titolo: "Costi AllService",
        testo:
          "Il magazzino AllService fattura ogni mese preparazione, spazio, entrata merce ed etichette. Importi senza IVA dal foglio prefattura. Non sono divisi per ordine e non stanno nel margine: sono una spesa a parte. Non seguono il filtro periodo.",
      }}
    >
      {errore ? (
        <p className="text-[13px] text-red">{errore}</p>
      ) : mesi == null ? (
        <p className="text-[13px]">Carico…</p>
      ) : ultimi.length === 0 ? (
        <p className="text-[13px]">Nessun mese registrato.</p>
      ) : (
        <ul className="space-y-2.5">
          {ultimi.map((m) => (
            <li key={m.month} className="text-[13px]">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-bold capitalize">
                  {new Date(`${m.month}T00:00:00`).toLocaleDateString("it-IT", {
                    month: "long",
                    year: "numeric",
                  })}
                  <InfoTip
                    spiegazione={{
                      titolo: "Voci del mese",
                      testo: "Le voci principali del foglio prefattura AllService.",
                      conto: [
                        ...VOCI_AS.map((v) => ({
                          segno: "+" as const,
                          etichetta: v.nome,
                          valore: eur(n(m[v.chiave] as number), 2),
                        })),
                        {
                          segno: "+" as const,
                          etichetta: "Altro (pallet, extra, varie)",
                          valore: eur(n(m.pallet_cost) + n(m.extra_cost) + n(m.varie_cost), 2),
                        },
                        {
                          segno: "=" as const,
                          etichetta: "Totale",
                          valore: eur(n(m.total_cost), 2),
                        },
                      ],
                    }}
                  />
                </span>
                <span className="font-bold tabular-nums">{eur(n(m.total_cost))}</span>
              </div>
              <span
                className="mt-1 block h-1.5 overflow-hidden rounded-full bg-paper-2"
                aria-hidden="true"
              >
                <span
                  className="bi-cresce block h-full rounded-full bg-violet"
                  style={{ width: `${(n(m.total_cost) / massimo) * 100}%` }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Riquadro>
  );
}

// ── Stati ────────────────────────────────────────────────────────────────────

const STATI: Record<string, string> = {
  COMPLETED: "Completato",
  FULL_SHIPPED: "Spedito",
  PARTIALLY_SHIPPED: "Spedito in parte",
  PAYMENT_ACCEPTED: "Pagato, da spedire",
  PENDING_PAYMENT: "In attesa di pagamento",
  WAITING_ACCEPTANCE: "Da accettare",
  ON_HOLD: "In attesa",
  SHIPPED: "Spedito",
  DELIVERED: "Consegnato",
  TO_SHIP: "Da spedire",
  PENDING: "In attesa",
  PROCESSING: "In lavorazione",
  REFUND: "Rimborsato",
  REFUNDED: "Rimborsato",
  CANCELLED: "Annullato",
  CANCELED: "Annullato",
  CANCELLED_BY_SELLER: "Annullato da noi",
  CANCELLED_BY_CUSTOMER: "Annullato dal cliente",
  CANCEL_REQUESTED: "Annullamento chiesto",
  PAYMENT_ERROR: "Errore pagamento",
};

export function Stati({ righe, esclusi }: { righe: BiStatusRow[]; esclusi: string[] }) {
  return (
    <Riquadro
      titolo="Ordini per stato"
      spiegazione={{
        titolo: "Ordini per stato",
        testo:
          "Tutti gli ordini del periodo, anche quelli fuori dai conti. Quelli barrati (annullati, rimborsati, errori) non entrano in fatturato e margine.",
      }}
    >
      <ul className="flex flex-wrap gap-2">
        {righe.map((r) => {
          const fuori = esclusi.includes(r.status);
          return (
            <li
              key={r.status}
              title={r.status}
              className="inline-flex h-[30px] items-center gap-2 rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-semibold"
            >
              <span className={fuori ? "line-through decoration-2" : ""}>
                {STATI[r.status] ?? r.status}
              </span>
              <span className="rounded-full bg-paper-2 px-1.5 text-[11.5px] font-bold tabular-nums">
                {intero(r.orders)}
              </span>
            </li>
          );
        })}
      </ul>
    </Riquadro>
  );
}
